import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Users, BookOpen, Video, FileText, Bell, Settings,
  Plus, Trash2, Edit, ChevronRight, LogOut, User,
  BarChart3, Calendar, Award, Sparkles, Menu, X,
  GraduationCap, ClipboardList, Download, CheckCircle2,
  FileSpreadsheet, UserCheck, MessageCircle, Upload, CreditCard, AlertCircle, Eye, EyeOff, ExternalLink, DollarSign, Star
} from 'lucide-react';
import { useAuth, api } from '../context/AuthContext';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import AdminMessagesTab from './AdminMessagesTab';

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Modal states
  const [showUserModal, setShowUserModal] = useState(false);
  const [showSubjectModal, setShowSubjectModal] = useState(false);
  const [showContentModal, setShowContentModal] = useState(false);
  const [showClassModal, setShowClassModal] = useState(false);
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  
  // Data states
  const [users, setUsers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [content, setContent] = useState([]);
  const [classes, setClasses] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [children, setChildren] = useState([]);

  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, []);

  useEffect(() => {
    if (activeTab === 'users') fetchUsers();
    if (activeTab === 'subjects') fetchSubjects();
    if (activeTab === 'content') { fetchContent(); fetchSubjects(); }
    if (activeTab === 'classes') { fetchClasses(); fetchSubjects(); }
    if (activeTab === 'quizzes') { fetchQuizzes(); fetchSubjects(); }
    if (activeTab === 'children') { fetchChildren(); fetchUsers(); fetchSubjects(); }
    if (activeTab === 'attendance') { fetchClasses(); fetchChildren(); }
  }, [activeTab]);

  const fetchDashboard = async () => {
    setLoadError(null);
    try {
      const { data } = await api.get('/api/dashboard/admin');
      setDashboardData(data);
    } catch (error) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      // Auto-logout on stale/invalid session so user gets a clean login prompt
      if (status === 401 || status === 403) {
        toast.error('Session expired. Please log in again.');
        try { await logout(); } catch (e) { /* ignore */ }
        navigate('/');
        return;
      }
      const msg = !error?.response
        ? 'Cannot reach server. Check your internet connection.'
        : (typeof detail === 'string' ? detail : `Failed to load (${status || 'unknown'}).`);
      setLoadError(msg);
      toast.error(msg);
      console.error('[admin dashboard]', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const { data } = await api.get('/api/admin/users');
      setUsers(data);
    } catch (error) {
      toast.error('Failed to load users');
    }
  };

  const fetchSubjects = async () => {
    try {
      const { data } = await api.get('/api/subjects');
      setSubjects(data);
    } catch (error) {
      toast.error('Failed to load subjects');
    }
  };

  const fetchContent = async () => {
    try {
      const { data } = await api.get('/api/content');
      setContent(data);
    } catch (error) {
      toast.error('Failed to load content');
    }
  };

  const fetchClasses = async () => {
    try {
      const { data } = await api.get('/api/classes');
      setClasses(data);
    } catch (error) {
      toast.error('Failed to load classes');
    }
  };

  const fetchQuizzes = async () => {
    try {
      const { data } = await api.get('/api/quizzes');
      setQuizzes(data);
    } catch (error) {
      toast.error('Failed to load quizzes');
    }
  };

  const fetchChildren = async () => {
    try {
      const { data } = await api.get('/api/children');
      setChildren(data);
    } catch (error) {
      toast.error('Failed to load children');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const menuItems = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'children', label: 'Children', icon: GraduationCap },
    { id: 'subjects', label: 'Subjects', icon: BookOpen },
    { id: 'content', label: 'Content', icon: FileText },
    { id: 'classes', label: 'Live Classes', icon: Video },
    { id: 'attendance', label: 'Attendance', icon: UserCheck },
    { id: 'quizzes', label: 'Quizzes', icon: ClipboardList },
    { id: 'watch-analytics', label: 'Watch Analytics', icon: Eye },
    { id: 'payments', label: 'Payments', icon: CreditCard },
    { id: 'pricing', label: 'Pricing', icon: DollarSign },
    { id: 'messages', label: 'Messages', icon: MessageCircle },
    { id: 'testimonials', label: 'Testimonials', icon: Star },
    { id: 'notifications', label: 'Notifications', icon: Bell },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-12 h-12 border-4 border-sky-blue border-t-transparent rounded-full"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform flex flex-col ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      }`}>
        <div className="p-6 border-b">
          <div className="flex items-center gap-3">
            <Sparkles className="w-8 h-8 text-sunset-orange" />
            <span className="font-fredoka text-xl font-bold text-gray-800">Craven-it Admin</span>
          </div>
        </div>
        
        <nav className="flex-1 overflow-y-auto p-4 space-y-1 pb-24">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-nunito font-semibold transition-all ${
                activeTab === item.id 
                  ? 'bg-sunset-orange text-white' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              data-testid={`nav-${item.id}`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </button>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t space-y-1">
          <a
            href="https://app.seesaw.me/"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center gap-3 px-4 py-3 text-gray-600 hover:bg-gray-100 rounded-xl font-nunito font-semibold"
            data-testid="admin-seesaw-link"
          >
            <ExternalLink className="w-5 h-5" />
            Open Seesaw
          </a>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-gray-600 hover:bg-gray-100 rounded-xl font-nunito font-semibold"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 min-h-screen">
        {/* Header */}
        <header className="bg-white shadow-sm sticky top-0 z-30">
          <div className="px-4 md:px-8 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-6 h-6" />
              </button>
              <h1 className="font-fredoka text-xl md:text-2xl font-bold text-gray-800 capitalize">
                {activeTab === 'overview' ? 'Dashboard Overview' : activeTab}
              </h1>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-sunset-orange/20 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-sunset-orange" />
              </div>
              <span className="font-nunito font-semibold text-gray-700 hidden md:block">{user?.name}</span>
            </div>
          </div>
        </header>

        <div className="p-4 md:p-8">
          {/* Error banner (visible when dashboard load failed) */}
          {loadError && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-2xl p-4 flex items-center justify-between gap-3" data-testid="admin-load-error">
              <div>
                <p className="font-fredoka font-semibold text-red-700">Couldn't load dashboard</p>
                <p className="font-nunito text-sm text-red-600">{loadError}</p>
              </div>
              <button
                onClick={fetchDashboard}
                className="px-4 py-2 rounded-full bg-red-600 hover:bg-red-700 text-white font-fredoka font-medium text-sm"
                data-testid="admin-retry-btn"
              >
                Retry
              </button>
            </div>
          )}

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {[
                  { label: 'Parents', value: dashboardData?.stats?.total_parents || 0, icon: Users, color: 'bg-sky-blue' },
                  { label: 'Children', value: dashboardData?.stats?.total_children || 0, icon: GraduationCap, color: 'bg-apple-green' },
                  { label: 'Subjects', value: dashboardData?.stats?.total_subjects || 0, icon: BookOpen, color: 'bg-sunny-yellow' },
                  { label: 'Content', value: dashboardData?.stats?.total_content || 0, icon: FileText, color: 'bg-sunset-orange' },
                  { label: 'Classes', value: dashboardData?.stats?.total_classes || 0, icon: Video, color: 'bg-grass-green' },
                  { label: 'Quizzes', value: dashboardData?.stats?.total_quizzes || 0, icon: ClipboardList, color: 'bg-sky-blue' },
                ].map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-2xl p-5 shadow-sm"
                  >
                    <div className={`w-10 h-10 ${stat.color}/20 rounded-xl flex items-center justify-center mb-3`}>
                      <stat.icon className={`w-5 h-5 ${stat.color.replace('bg-', 'text-')}`} />
                    </div>
                    <p className="font-fredoka text-2xl font-bold text-gray-800">{stat.value}</p>
                    <p className="font-nunito text-sm text-gray-500">{stat.label}</p>
                  </motion.div>
                ))}
              </div>

              {/* Upcoming Classes & Recent Submissions */}
              <div className="grid lg:grid-cols-2 gap-6">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-white rounded-2xl p-6 shadow-sm"
                >
                  <h2 className="font-fredoka text-xl font-bold text-gray-800 mb-4">Upcoming Classes</h2>
                  {dashboardData?.upcoming_classes?.length > 0 ? (
                    <div className="space-y-3">
                      {dashboardData.upcoming_classes.map((cls) => (
                        <div key={cls.id} className="p-4 bg-gray-50 rounded-xl">
                          <p className="font-nunito font-semibold text-gray-800">{cls.title}</p>
                          <p className="font-nunito text-sm text-gray-500">{cls.subject} • {cls.grade}</p>
                          <p className="font-nunito text-sm text-sky-blue mt-1">
                            {new Date(cls.scheduled_at).toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 font-nunito">No upcoming classes</p>
                  )}
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="bg-white rounded-2xl p-6 shadow-sm"
                >
                  <h2 className="font-fredoka text-xl font-bold text-gray-800 mb-4">Recent Quiz Submissions</h2>
                  {dashboardData?.recent_submissions?.length > 0 ? (
                    <div className="space-y-3">
                      {dashboardData.recent_submissions.slice(0, 5).map((sub, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                          <div>
                            <p className="font-nunito font-semibold text-gray-800">{sub.child_name}</p>
                            <p className="font-nunito text-sm text-gray-500">{sub.quiz_title}</p>
                          </div>
                          <span className={`font-fredoka font-bold ${
                            sub.percentage >= 80 ? 'text-apple-green' : 
                            sub.percentage >= 60 ? 'text-sunny-yellow' : 'text-sunset-orange'
                          }`}>
                            {Math.round(sub.percentage)}%
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 font-nunito">No recent submissions</p>
                  )}
                </motion.div>
              </div>
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <UsersTab 
              users={users} 
              onRefresh={fetchUsers}
              showModal={showUserModal}
              setShowModal={setShowUserModal}
            />
          )}

          {/* Children Tab */}
          {activeTab === 'children' && (
            <ChildrenTab 
              childrenList={children}
              users={users.filter(u => u.role === 'parent')}
              subjects={subjects}
              onRefresh={fetchChildren}
            />
          )}

          {/* Subjects Tab */}
          {activeTab === 'subjects' && (
            <SubjectsTab 
              subjects={subjects}
              onRefresh={fetchSubjects}
              showModal={showSubjectModal}
              setShowModal={setShowSubjectModal}
            />
          )}

          {/* Content Tab */}
          {activeTab === 'content' && (
            <ContentTab 
              content={content}
              subjects={subjects}
              onRefresh={fetchContent}
              showModal={showContentModal}
              setShowModal={setShowContentModal}
            />
          )}

          {/* Classes Tab */}
          {activeTab === 'classes' && (
            <ClassesTab 
              classes={classes}
              subjects={subjects}
              onRefresh={fetchClasses}
              showModal={showClassModal}
              setShowModal={setShowClassModal}
            />
          )}

          {/* Attendance Tab */}
          {activeTab === 'attendance' && (
            <AttendanceTab
              classes={classes}
              childrenList={children}
            />
          )}

          {/* Messages Tab */}
          {activeTab === 'messages' && (
            <AdminMessagesTab />
          )}

          {/* Payments Tab */}
          {activeTab === 'payments' && (
            <PaymentsTab />
          )}

          {/* Pricing Tab */}
          {activeTab === 'pricing' && (
            <PricingTab />
          )}

          {/* Quizzes Tab */}
          {activeTab === 'quizzes' && (
            <QuizzesTab 
              quizzes={quizzes}
              subjects={subjects}
              onRefresh={fetchQuizzes}
              showModal={showQuizModal}
              setShowModal={setShowQuizModal}
            />
          )}

          {/* Watch Analytics Tab */}
          {activeTab === 'watch-analytics' && (
            <WatchAnalyticsTab />
          )}

          {/* Testimonials Tab */}
          {activeTab === 'testimonials' && (
            <TestimonialsTab />
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <NotificationsTab 
              showModal={showNotificationModal}
              setShowModal={setShowNotificationModal}
            />
          )}
        </div>
      </main>
    </div>
  );
}

// Users Tab Component
function UsersTab({ users, onRefresh, showModal, setShowModal }) {
  const [formData, setFormData] = useState({ name: '', email: '', password: '', role: 'parent' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await api.post('/api/admin/users', formData);
      toast.success('User created successfully!');
      setShowModal(false);
      setFormData({ name: '', email: '', password: '', role: 'parent' });
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await api.delete(`/api/admin/users/${userId}`);
      toast.success('User deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6 flex-wrap gap-2">
        <h2 className="font-fredoka text-xl font-bold text-gray-800">Manage Users</h2>
        <div className="flex gap-2">
          <CsvImportButton
            endpoint="/api/admin/import/parents"
            label="Import parents CSV"
            testid="import-parents-csv"
            template="name,email,password"
            onDone={onRefresh}
          />
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 bg-sunset-orange text-white px-4 py-2 rounded-xl font-nunito font-semibold hover:bg-orange-600 transition-colors"
            data-testid="add-user-btn"
          >
            <Plus className="w-5 h-5" /> Add User
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-4 text-left font-nunito font-semibold text-gray-600">Name</th>
              <th className="px-6 py-4 text-left font-nunito font-semibold text-gray-600">Email</th>
              <th className="px-6 py-4 text-left font-nunito font-semibold text-gray-600">Role</th>
              <th className="px-6 py-4 text-right font-nunito font-semibold text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {users.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 font-nunito text-gray-800">{user.name}</td>
                <td className="px-6 py-4 font-nunito text-gray-600">{user.email}</td>
                <td className="px-6 py-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-nunito font-semibold ${
                    user.role === 'admin' ? 'bg-sunset-orange/20 text-sunset-orange' : 'bg-sky-blue/20 text-sky-blue'
                  }`}>
                    {user.role}
                  </span>
                </td>
                <td className="px-6 py-4 text-right">
                  <button 
                    onClick={() => handleDelete(user.id)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-fredoka text-xl">Add New User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="role">Role</Label>
              <select
                id="role"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="w-full border rounded-lg px-3 py-2"
              >
                <option value="parent">Parent</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-sunset-orange text-white py-3 rounded-xl font-nunito font-semibold hover:bg-orange-600 disabled:opacity-50"
            >
              {isSubmitting ? 'Creating...' : 'Create User'}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Children Tab Component
function ChildrenTab({ childrenList, users, subjects, onRefresh }) {
  const [showModal, setShowModal] = useState(false);
  const [assignChild, setAssignChild] = useState(null);
  const [formData, setFormData] = useState({ name: '', grade: '', parent_id: '', date_of_birth: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await api.post('/api/children', formData);
      toast.success('Child added successfully!');
      setShowModal(false);
      setFormData({ name: '', grade: '', parent_id: '', date_of_birth: '' });
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add child');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (childId) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await api.delete(`/api/children/${childId}`);
      toast.success('Child removed');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const assignSubject = async (childId, subjectId) => {
    try {
      await api.post(`/api/children/${childId}/subjects/${subjectId}`);
      toast.success('Subject assigned!');
      onRefresh();
    } catch {
      toast.error('Failed to assign subject');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6 flex-wrap gap-2">
        <h2 className="font-fredoka text-xl font-bold text-gray-800">Manage Children</h2>
        <div className="flex gap-2">
          <CsvImportButton
            endpoint="/api/admin/import/children"
            label="Import children CSV"
            testid="import-children-csv"
            template="name,grade,parent_email"
            onDone={onRefresh}
          />
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 bg-sunset-orange text-white px-4 py-2 rounded-xl font-nunito font-semibold hover:bg-orange-600"
            data-testid="add-child-btn"
          >
            <Plus className="w-5 h-5" /> Add Child
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {childrenList.map((child) => (
          <div key={child.id} className="bg-white rounded-2xl p-6 shadow-sm" data-testid={`child-card-${child.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-fredoka text-lg font-bold text-gray-800">{child.name}</h3>
                <p className="font-nunito text-gray-500">Grade: {child.grade}</p>
                <p className="font-nunito text-sm text-gray-400 mt-2">Points: {child.points}</p>
                <p className="font-nunito text-xs text-gray-400 mt-1">
                  Subjects: {child.subjects?.length || 0}
                </p>
              </div>
              <button 
                onClick={() => handleDelete(child.id)}
                className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
            <button
              onClick={() => setAssignChild(child)}
              className="mt-4 w-full text-sm bg-sky-blue/10 text-sky-blue py-2 rounded-lg font-nunito font-semibold hover:bg-sky-blue/20"
              data-testid={`assign-subjects-${child.id}`}
            >
              Assign Subjects
            </button>
          </div>
        ))}
      </div>

      {/* Add Child Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-fredoka text-xl">Add Child</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Child's Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="grade">Grade</Label>
              <select
                id="grade"
                value={formData.grade}
                onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                className="w-full border rounded-lg px-3 py-2"
                required
              >
                <option value="">Select Grade</option>
                <option value="K">Kindergarten</option>
                <option value="1">1st Grade</option>
                <option value="2">2nd Grade</option>
              </select>
            </div>
            <div>
              <Label htmlFor="parent_id">Parent</Label>
              <select
                id="parent_id"
                value={formData.parent_id}
                onChange={(e) => setFormData({ ...formData, parent_id: e.target.value })}
                className="w-full border rounded-lg px-3 py-2"
                required
              >
                <option value="">Select Parent</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>{user.name} ({user.email})</option>
                ))}
              </select>
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-sunset-orange text-white py-3 rounded-xl font-nunito font-semibold hover:bg-orange-600 disabled:opacity-50"
            >
              {isSubmitting ? 'Adding...' : 'Add Child'}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Subjects Modal */}
      <Dialog open={!!assignChild} onOpenChange={(open) => { if (!open) setAssignChild(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-fredoka text-xl">
              Assign Subjects — {assignChild?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="font-nunito text-sm text-gray-500">
              Click a subject to assign it to this child.
            </p>
            {subjects.filter((s) => s.grade === assignChild?.grade).length === 0 && (
              <p className="font-nunito text-sm text-gray-400">
                No subjects available for grade {assignChild?.grade}. Create one first.
              </p>
            )}
            {subjects.filter((s) => s.grade === assignChild?.grade).map((subject) => {
              const isAssigned = assignChild?.subjects?.includes(subject.id);
              return (
                <button
                  key={subject.id}
                  disabled={isAssigned}
                  onClick={() => { assignSubject(assignChild.id, subject.id); setAssignChild(null); }}
                  className={`w-full text-left p-3 rounded-lg border-2 flex items-center justify-between ${
                    isAssigned ? 'border-apple-green bg-apple-green/10' : 'border-gray-200 hover:border-sunset-orange'
                  }`}
                  style={{ borderLeftWidth: '4px', borderLeftColor: subject.color }}
                  data-testid={`assign-subject-${subject.id}`}
                >
                  <div>
                    <p className="font-nunito font-semibold text-gray-800">{subject.name}</p>
                    {subject.description && <p className="text-xs text-gray-500">{subject.description}</p>}
                  </div>
                  {isAssigned && <CheckCircle2 className="w-5 h-5 text-apple-green" />}
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Subjects Tab Component
function SubjectsTab({ subjects, onRefresh, showModal, setShowModal }) {
  const [formData, setFormData] = useState({ name: '', description: '', grade: '', color: '#87CEEB' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await api.post('/api/subjects', formData);
      toast.success('Subject created!');
      setShowModal(false);
      setFormData({ name: '', description: '', grade: '', color: '#87CEEB' });
      onRefresh();
    } catch (error) {
      toast.error('Failed to create subject');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (subjectId) => {
    if (!window.confirm('Delete this subject?')) return;
    try {
      await api.delete(`/api/subjects/${subjectId}`);
      toast.success('Subject deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-fredoka text-xl font-bold text-gray-800">Manage Subjects</h2>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-sunset-orange text-white px-4 py-2 rounded-xl font-nunito font-semibold hover:bg-orange-600"
        >
          <Plus className="w-5 h-5" /> Add Subject
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {subjects.map((subject) => (
          <div 
            key={subject.id} 
            className="bg-white rounded-2xl p-6 shadow-sm border-l-4"
            style={{ borderLeftColor: subject.color }}
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-fredoka text-lg font-bold text-gray-800">{subject.name}</h3>
                <p className="font-nunito text-gray-500">Grade: {subject.grade}</p>
                {subject.description && (
                  <p className="font-nunito text-sm text-gray-400 mt-2">{subject.description}</p>
                )}
              </div>
              <button 
                onClick={() => handleDelete(subject.id)}
                className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-fredoka text-xl">Add Subject</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Subject Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Reading, Math"
                required
              />
            </div>
            <div>
              <Label htmlFor="grade">Grade</Label>
              <select
                id="grade"
                value={formData.grade}
                onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                className="w-full border rounded-lg px-3 py-2"
                required
              >
                <option value="">Select Grade</option>
                <option value="K">Kindergarten</option>
                <option value="1">1st Grade</option>
                <option value="2">2nd Grade</option>
              </select>
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="color">Color</Label>
              <input
                type="color"
                id="color"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                className="w-full h-10 rounded-lg cursor-pointer"
              />
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-sunset-orange text-white py-3 rounded-xl font-nunito font-semibold hover:bg-orange-600 disabled:opacity-50"
            >
              {isSubmitting ? 'Creating...' : 'Create Subject'}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Content Tab Component
function ContentTab({ content, subjects, onRefresh, showModal, setShowModal }) {
  const [formData, setFormData] = useState({ 
    title: '', description: '', content_type: 'video', url: '', subject_id: '', grade: '', order: 0 
  });
  const [uploadMode, setUploadMode] = useState('url'); // 'url' or 'file'
  const [uploading, setUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewItem, setPreviewItem] = useState(null); // opened via "View" action
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const buildFullUrl = (u) => (u?.startsWith('http') || !u) ? u : `${BACKEND}${u}`;

  const filtered = content.filter((c) => {
    if (typeFilter !== 'all' && c.content_type !== typeFilter) return false;
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (c.title || '').toLowerCase().includes(q) || (c.description || '').toLowerCase().includes(q);
  });

  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingFileName, setUploadingFileName] = useState('');

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadProgress(0);
    setUploadingFileName(file.name);

    try {
      const token = localStorage.getItem('craven_access_token');
      const data = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${BACKEND}/api/uploads`);
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.withCredentials = true;
        xhr.upload.onprogress = (evt) => {
          if (evt.lengthComputable) {
            setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try { resolve(JSON.parse(xhr.responseText)); }
            catch { reject(new Error('Invalid server response')); }
          } else {
            let detail = 'Upload failed';
            try { detail = JSON.parse(xhr.responseText).detail || detail; } catch (_) { /* keep default */ }
            reject(new Error(detail));
          }
        };
        xhr.onerror = () => reject(new Error('Network error during upload'));
        xhr.onabort = () => reject(new Error('Upload cancelled'));
        const fd = new FormData();
        fd.append('file', file);
        xhr.send(fd);
      });
      setFormData((prev) => ({ ...prev, url: `${BACKEND}${data.url}` }));
      setUploadProgress(100);
      toast.success(`Uploaded: ${data.filename}`);
    } catch (err) {
      toast.error(err.message || 'Upload failed');
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Guard against empty URL saves — main root cause of broken content records
    if (!formData.url || !formData.url.trim()) {
      toast.error(uploadMode === 'file' ? 'Please upload a file before saving' : 'Please enter a content URL');
      return;
    }
    setIsSubmitting(true);
    try {
      await api.post('/api/content', formData);
      toast.success('Content added!');
      setShowModal(false);
      setFormData({ title: '', description: '', content_type: 'video', url: '', subject_id: '', grade: '', order: 0 });
      setUploadMode('url');
      onRefresh();
    } catch (error) {
      toast.error('Failed to add content');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (contentId) => {
    if (!window.confirm('Delete this content?')) return;
    try {
      await api.delete(`/api/content/${contentId}`);
      toast.success('Content deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-fredoka text-xl font-bold text-gray-800">Manage Content</h2>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-sunset-orange text-white px-4 py-2 rounded-xl font-nunito font-semibold hover:bg-orange-600"
          data-testid="add-content-btn"
        >
          <Plus className="w-5 h-5" /> Add Content
        </button>
      </div>

      {/* Search + type filter row */}
      <div className="flex items-center gap-3 flex-wrap">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by title or description…"
          className="flex-1 min-w-[240px] rounded-full border border-gray-200 focus:border-sunset-orange focus:outline-none px-4 py-2.5 font-nunito text-sm"
          data-testid="content-search"
        />
        <div className="flex gap-1 bg-white rounded-full border border-gray-200 p-1">
          {[
            { id: 'all', label: 'All' },
            { id: 'video', label: '🎥 Video' },
            { id: 'pdf', label: '📄 PDF' },
            { id: 'activity', label: '🎲 Activity' },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setTypeFilter(f.id)}
              className={`px-4 py-1.5 rounded-full font-fredoka text-xs transition-all ${
                typeFilter === f.id ? 'bg-sunset-orange text-white' : 'text-gray-600 hover:bg-gray-50'
              }`}
              data-testid={`content-filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <span className="text-xs font-nunito text-gray-500 whitespace-nowrap">
          Showing {filtered.length} of {content.length}
        </span>
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-4 text-left font-nunito font-semibold text-gray-600">Content</th>
              <th className="px-6 py-4 text-left font-nunito font-semibold text-gray-600">Type</th>
              <th className="px-6 py-4 text-left font-nunito font-semibold text-gray-600">Subject</th>
              <th className="px-6 py-4 text-left font-nunito font-semibold text-gray-600">Grade</th>
              <th className="px-6 py-4 text-right font-nunito font-semibold text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-12 text-center">
                  <BookOpen className="w-10 h-10 text-gray-200 mx-auto mb-2" />
                  <p className="font-nunito text-gray-400 text-sm">
                    {content.length === 0 ? 'No content yet — click Add Content to upload your first lesson.' : 'No content matches your search.'}
                  </p>
                </td>
              </tr>
            ) : filtered.map((item) => {
              const Icon = item.content_type === 'video' ? Video : item.content_type === 'pdf' ? FileText : BookOpen;
              const iconBg = item.content_type === 'video' ? 'bg-sky-blue/15 text-sky-blue' : item.content_type === 'pdf' ? 'bg-sunset-orange/15 text-sunset-orange' : 'bg-apple-green/15 text-apple-green';
              const subject = subjects.find((s) => s.id === item.subject_id);
              return (
                <tr key={item.id} className="hover:bg-gray-50" data-testid={`content-row-${item.id}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-fredoka font-semibold text-gray-800 text-sm truncate max-w-xs">{item.title}</p>
                        {item.description && <p className="font-nunito text-xs text-gray-500 truncate max-w-xs">{item.description}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-nunito font-semibold capitalize ${
                      item.content_type === 'video' ? 'bg-sky-blue/20 text-sky-blue' :
                      item.content_type === 'pdf' ? 'bg-sunset-orange/20 text-sunset-orange' :
                      'bg-apple-green/20 text-apple-green'
                    }`}>
                      {item.content_type}
                    </span>
                  </td>
                  <td className="px-6 py-4 font-nunito text-gray-600 text-sm">{subject?.name || '—'}</td>
                  <td className="px-6 py-4 font-nunito text-gray-600 text-sm">{item.grade}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setPreviewItem(item)}
                        className="p-2 text-sky-blue hover:bg-sky-blue/10 rounded-lg"
                        title="Preview content"
                        data-testid={`preview-content-${item.id}`}
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                      {item.url && (
                        <a
                          href={buildFullUrl(item.url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                          title="Open in new tab"
                          data-testid={`open-content-${item.id}`}
                        >
                          <ExternalLink className="w-5 h-5" />
                        </a>
                      )}
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                        title="Delete"
                        data-testid={`delete-content-${item.id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Preview dialog for any existing content item */}
      <Dialog open={!!previewItem} onOpenChange={(v) => { if (!v) setPreviewItem(null); }}>
        <DialogContent className="sm:max-w-3xl max-h-[92vh] flex flex-col" data-testid="content-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-fredoka text-xl flex items-center gap-2">
              {previewItem?.content_type === 'video' && <Video className="w-5 h-5 text-sky-blue" />}
              {previewItem?.content_type === 'pdf' && <FileText className="w-5 h-5 text-sunset-orange" />}
              {previewItem?.content_type === 'activity' && <BookOpen className="w-5 h-5 text-apple-green" />}
              {previewItem?.title}
            </DialogTitle>
          </DialogHeader>
          {previewItem && (
            <div className="space-y-3 pt-1 flex-1 min-h-0 flex flex-col">
              {previewItem.description && (
                <p className="font-nunito text-sm text-gray-600">{previewItem.description}</p>
              )}
              <div className="flex-1 min-h-0" style={{ minHeight: '65vh' }}>
                <ContentPreview url={buildFullUrl(previewItem.url)} contentType={previewItem.content_type} tall />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-fredoka text-xl">Add Content</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="content_type">Type</Label>
              <select
                id="content_type"
                value={formData.content_type}
                onChange={(e) => setFormData({ ...formData, content_type: e.target.value })}
                className="w-full border rounded-lg px-3 py-2"
              >
                <option value="video">Video (YouTube/Vimeo)</option>
                <option value="pdf">PDF Document</option>
                <option value="activity">Activity</option>
              </select>
            </div>
            <div>
              <Label htmlFor="url">Source</Label>
              <div className="flex gap-2 mb-2">
                <button
                  type="button"
                  onClick={() => setUploadMode('url')}
                  className={`flex-1 py-2 rounded-lg text-sm font-nunito font-semibold ${uploadMode === 'url' ? 'bg-sunset-orange text-white' : 'bg-gray-100 text-gray-600'}`}
                  data-testid="content-mode-url"
                >
                  Paste URL
                </button>
                <button
                  type="button"
                  onClick={() => setUploadMode('file')}
                  className={`flex-1 py-2 rounded-lg text-sm font-nunito font-semibold ${uploadMode === 'file' ? 'bg-sunset-orange text-white' : 'bg-gray-100 text-gray-600'}`}
                  data-testid="content-mode-file"
                >
                  Upload File
                </button>
              </div>
              {uploadMode === 'url' ? (
                <Input
                  id="url"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  placeholder="YouTube, Vimeo, or PDF link"
                  required
                  data-testid="content-url-input"
                />
              ) : (
                <div className="space-y-2">
                  <input
                    id="file-upload"
                    type="file"
                    accept={formData.content_type === 'pdf' ? '.pdf,application/pdf' : formData.content_type === 'video' ? 'video/*' : '*/*'}
                    onChange={handleFileUpload}
                    disabled={uploading}
                    className="w-full border rounded-lg px-3 py-2 text-sm disabled:opacity-50"
                    data-testid="content-file-input"
                  />
                  {uploading && (
                    <div className="space-y-1.5 pt-1" data-testid="upload-progress">
                      <div className="flex items-center justify-between text-xs font-nunito">
                        <span className="text-gray-600 truncate max-w-[70%]" title={uploadingFileName}>
                          Uploading {uploadingFileName}…
                        </span>
                        <span className="font-fredoka font-bold text-sunset-orange" data-testid="upload-progress-percent">
                          {uploadProgress}%
                        </span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <motion.div
                          className="h-full bg-gradient-to-r from-sunset-orange to-[#F59E0B]"
                          initial={{ width: 0 }}
                          animate={{ width: `${uploadProgress}%` }}
                          transition={{ duration: 0.15, ease: 'easeOut' }}
                          data-testid="upload-progress-bar"
                        />
                      </div>
                    </div>
                  )}
                  {formData.url && !uploading && (
                    <p className="text-sm text-apple-green break-all">Uploaded ✓</p>
                  )}
                </div>
              )}
              {/* Preview panel — shows immediately after upload or when URL entered */}
              {formData.url && !uploading && (
                <ContentPreview url={formData.url} contentType={formData.content_type} />
              )}
            </div>
            <div>
              <Label htmlFor="subject_id">Subject</Label>
              <select
                id="subject_id"
                value={formData.subject_id}
                onChange={(e) => setFormData({ ...formData, subject_id: e.target.value })}
                className="w-full border rounded-lg px-3 py-2"
                required
              >
                <option value="">Select Subject</option>
                {subjects.map((s) => (
                  <option key={s.id} value={s.id}>{s.name} ({s.grade})</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="grade">Grade</Label>
              <select
                id="grade"
                value={formData.grade}
                onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                className="w-full border rounded-lg px-3 py-2"
                required
              >
                <option value="">Select Grade</option>
                <option value="K">Kindergarten</option>
                <option value="1">1st Grade</option>
                <option value="2">2nd Grade</option>
              </select>
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-sunset-orange text-white py-3 rounded-xl font-nunito font-semibold hover:bg-orange-600 disabled:opacity-50"
            >
              {isSubmitting ? 'Adding...' : 'Add Content'}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Classes Tab Component  
function ClassesTab({ classes, subjects, onRefresh, showModal, setShowModal }) {
  const [formData, setFormData] = useState({
    title: '', description: '', subject_id: '', grade: '', 
    scheduled_at: '', duration_minutes: 60, google_meet_link: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Recurrence state
  const [isRecurring, setIsRecurring] = useState(false);
  const [daysOfWeek, setDaysOfWeek] = useState([]); // 0=Mon..6=Sun
  const [timeOfDay, setTimeOfDay] = useState('10:00');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [occurrences, setOccurrences] = useState(8);

  const toggleDay = (d) => {
    setDaysOfWeek((prev) => prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (isRecurring) {
        if (daysOfWeek.length === 0) {
          toast.error('Pick at least one day of the week');
          setIsSubmitting(false);
          return;
        }
        const { data } = await api.post('/api/classes/recurring', {
          title: formData.title,
          description: formData.description,
          subject_id: formData.subject_id,
          grade: formData.grade,
          days_of_week: daysOfWeek,
          time_of_day: timeOfDay,
          start_date: startDate,
          occurrences: parseInt(occurrences, 10) || 8,
          duration_minutes: formData.duration_minutes,
          google_meet_link: formData.google_meet_link,
        });
        toast.success(`${data.created} recurring classes scheduled! 🎉`);
      } else {
        await api.post('/api/classes', {
          ...formData,
          scheduled_at: new Date(formData.scheduled_at).toISOString()
        });
        toast.success('Class scheduled!');
      }
      setShowModal(false);
      setFormData({ title: '', description: '', subject_id: '', grade: '', scheduled_at: '', duration_minutes: 60, google_meet_link: '' });
      setIsRecurring(false);
      setDaysOfWeek([]);
      onRefresh();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to schedule class');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (classId) => {
    if (!window.confirm('Delete this class?')) return;
    try {
      await api.delete(`/api/classes/${classId}`);
      toast.success('Class deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-fredoka text-xl font-bold text-gray-800">Live Classes</h2>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-sunset-orange text-white px-4 py-2 rounded-xl font-nunito font-semibold hover:bg-orange-600"
        >
          <Plus className="w-5 h-5" /> Schedule Class
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {classes.map((cls) => (
          <div key={cls.id} className="bg-white rounded-2xl p-6 shadow-sm">
            <div className="flex items-start justify-between mb-3">
              <span className={`px-3 py-1 rounded-full text-xs font-nunito font-semibold ${
                cls.status === 'scheduled' ? 'bg-sky-blue/20 text-sky-blue' :
                cls.status === 'completed' ? 'bg-apple-green/20 text-apple-green' :
                'bg-sunny-yellow/20 text-sunny-yellow'
              }`}>
                {cls.status}
              </span>
              <button 
                onClick={() => handleDelete(cls.id)}
                className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <h3 className="font-fredoka text-lg font-bold text-gray-800">{cls.title}</h3>
            <p className="font-nunito text-gray-500 text-sm">{cls.subject_name} • {cls.grade}</p>
            <p className="font-nunito text-sky-blue text-sm mt-2">
              {new Date(cls.scheduled_at).toLocaleString()}
            </p>
            <a 
              href={cls.google_meet_link}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-2 text-sunset-orange hover:underline font-nunito text-sm"
            >
              <Video className="w-4 h-4" /> Join Link
            </a>
          </div>
        ))}
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-fredoka text-xl">Schedule Class</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="title">Class Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="subject_id">Subject</Label>
              <select
                id="subject_id"
                value={formData.subject_id}
                onChange={(e) => setFormData({ ...formData, subject_id: e.target.value })}
                className="w-full border rounded-lg px-3 py-2"
                required
              >
                <option value="">Select Subject</option>
                {subjects.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="grade">Grade</Label>
              <select
                id="grade"
                value={formData.grade}
                onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                className="w-full border rounded-lg px-3 py-2"
                required
              >
                <option value="">Select Grade</option>
                <option value="K">Kindergarten</option>
                <option value="1">1st Grade</option>
                <option value="2">2nd Grade</option>
              </select>
            </div>
            {/* Recurring toggle */}
            <div className="flex items-center gap-3 p-3 rounded-xl bg-sky-blue/10 border border-sky-blue/20">
              <input
                type="checkbox"
                id="is-recurring"
                checked={isRecurring}
                onChange={(e) => setIsRecurring(e.target.checked)}
                className="w-4 h-4 accent-sunset-orange"
                data-testid="recurring-toggle"
              />
              <label htmlFor="is-recurring" className="font-nunito text-sm font-semibold text-gray-700 cursor-pointer flex-1">
                🔁 Repeat weekly
                <span className="block text-xs text-gray-500 font-normal">e.g., every Friday at 10 AM for 8 weeks</span>
              </label>
            </div>

            {!isRecurring ? (
              <div>
                <Label htmlFor="scheduled_at">Date & Time</Label>
                <Input
                  id="scheduled_at"
                  type="datetime-local"
                  value={formData.scheduled_at}
                  onChange={(e) => setFormData({ ...formData, scheduled_at: e.target.value })}
                  required
                />
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <Label>Days of the week</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {[
                      { d: 0, label: 'Mon' },
                      { d: 1, label: 'Tue' },
                      { d: 2, label: 'Wed' },
                      { d: 3, label: 'Thu' },
                      { d: 4, label: 'Fri' },
                      { d: 5, label: 'Sat' },
                      { d: 6, label: 'Sun' },
                    ].map(({ d, label }) => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => toggleDay(d)}
                        className={`px-3 py-1.5 rounded-full text-xs font-fredoka font-semibold transition-all ${
                          daysOfWeek.includes(d)
                            ? 'bg-sunset-orange text-white shadow-sm'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        data-testid={`recurring-day-${label.toLowerCase()}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="time-of-day">Time (24h)</Label>
                    <Input
                      id="time-of-day"
                      type="time"
                      value={timeOfDay}
                      onChange={(e) => setTimeOfDay(e.target.value)}
                      required
                      data-testid="recurring-time"
                    />
                  </div>
                  <div>
                    <Label htmlFor="start-date">Start date</Label>
                    <Input
                      id="start-date"
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      required
                      data-testid="recurring-start-date"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="occurrences">Number of classes to schedule</Label>
                  <Input
                    id="occurrences"
                    type="number"
                    min={1}
                    max={52}
                    value={occurrences}
                    onChange={(e) => setOccurrences(e.target.value)}
                    required
                    data-testid="recurring-occurrences"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Max 52 (roughly one year). Total = classes ÷ days-per-week weeks of content.
                  </p>
                </div>
              </div>
            )}
            <div>
              <Label htmlFor="google_meet_link">Google Meet Link</Label>
              <Input
                id="google_meet_link"
                value={formData.google_meet_link}
                onChange={(e) => setFormData({ ...formData, google_meet_link: e.target.value })}
                placeholder="https://meet.google.com/..."
                required
              />
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-sunset-orange text-white py-3 rounded-xl font-nunito font-semibold hover:bg-orange-600 disabled:opacity-50"
              data-testid="save-class-btn"
            >
              {isSubmitting
                ? 'Scheduling...'
                : isRecurring
                  ? `Schedule ${occurrences} classes`
                  : 'Schedule Class'}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// Shared quiz form fields (used by both Create and Edit dialogs)
function QuizForm({ formData, setFormData, subjects, onSubmit, isSubmitting, submitLabel }) {
  const addQuestion = () => {
    setFormData({
      ...formData,
      questions: [...formData.questions, { question: '', options: ['', '', '', ''], correct_answer: 0, points: 10 }]
    });
  };

  const updateQuestion = (index, field, value) => {
    const newQuestions = [...formData.questions];
    newQuestions[index][field] = value;
    setFormData({ ...formData, questions: newQuestions });
  };

  const updateOption = (qIndex, oIndex, value) => {
    const newQuestions = [...formData.questions];
    newQuestions[qIndex].options[oIndex] = value;
    setFormData({ ...formData, questions: newQuestions });
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="title">Quiz Title</Label>
          <Input
            id="title"
            data-testid="quiz-form-title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
          />
        </div>
        <div>
          <Label htmlFor="subject_id">Subject</Label>
          <select
            id="subject_id"
            data-testid="quiz-form-subject"
            value={formData.subject_id}
            onChange={(e) => setFormData({ ...formData, subject_id: e.target.value })}
            className="w-full border rounded-lg px-3 py-2"
            required
          >
            <option value="">Select Subject</option>
            {subjects.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="grade">Grade</Label>
          <select
            id="grade"
            data-testid="quiz-form-grade"
            value={formData.grade}
            onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
            className="w-full border rounded-lg px-3 py-2"
            required
          >
            <option value="">Select Grade</option>
            <option value="K">Kindergarten</option>
            <option value="1">1st Grade</option>
            <option value="2">2nd Grade</option>
          </select>
        </div>
        <div>
          <Label htmlFor="time_limit">Time Limit (minutes)</Label>
          <Input
            id="time_limit"
            data-testid="quiz-form-time-limit"
            type="number"
            value={formData.time_limit_minutes}
            onChange={(e) => setFormData({ ...formData, time_limit_minutes: parseInt(e.target.value) })}
          />
        </div>
      </div>

      <div className="border-t pt-4">
        <h3 className="font-nunito font-semibold mb-3">Questions</h3>
        {formData.questions.map((q, qIndex) => (
          <div key={qIndex} className="bg-gray-50 p-4 rounded-xl mb-4">
            <div className="mb-3">
              <Label>Question {qIndex + 1}</Label>
              <Input
                data-testid={`quiz-question-${qIndex}`}
                value={q.question}
                onChange={(e) => updateQuestion(qIndex, 'question', e.target.value)}
                placeholder="Enter question"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {q.options.map((opt, oIndex) => (
                <Input
                  key={oIndex}
                  data-testid={`quiz-option-${qIndex}-${oIndex}`}
                  value={opt}
                  onChange={(e) => updateOption(qIndex, oIndex, e.target.value)}
                  placeholder={`Option ${oIndex + 1}`}
                  required
                />
              ))}
            </div>
            <div className="flex gap-4">
              <div className="flex-1">
                <Label>Correct Answer</Label>
                <select
                  data-testid={`quiz-correct-${qIndex}`}
                  value={q.correct_answer}
                  onChange={(e) => updateQuestion(qIndex, 'correct_answer', parseInt(e.target.value))}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  {q.options.map((_, i) => (
                    <option key={i} value={i}>Option {i + 1}</option>
                  ))}
                </select>
              </div>
              <div className="w-24">
                <Label>Points</Label>
                <Input
                  data-testid={`quiz-points-${qIndex}`}
                  type="number"
                  value={q.points}
                  onChange={(e) => updateQuestion(qIndex, 'points', parseInt(e.target.value))}
                />
              </div>
            </div>
          </div>
        ))}
        <button
          type="button"
          data-testid="quiz-add-question-btn"
          onClick={addQuestion}
          className="w-full py-2 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-sunset-orange hover:text-sunset-orange transition-colors"
        >
          + Add Question
        </button>
      </div>

      <button
        type="submit"
        data-testid="quiz-form-submit-btn"
        disabled={isSubmitting}
        className="w-full bg-sunset-orange text-white py-3 rounded-xl font-nunito font-semibold hover:bg-orange-600 disabled:opacity-50"
      >
        {isSubmitting ? 'Saving...' : submitLabel}
      </button>
    </form>
  );
}

// Quizzes Tab Component
function QuizzesTab({ quizzes, subjects, onRefresh, showModal, setShowModal }) {
  const emptyForm = {
    title: '', description: '', subject_id: '', grade: '', time_limit_minutes: 30,
    questions: [{ question: '', options: ['', '', '', ''], correct_answer: 0, points: 10 }]
  };
  const [formData, setFormData] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewQuiz, setViewQuiz] = useState(null);
  const [editQuiz, setEditQuiz] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await api.post('/api/quizzes', formData);
      toast.success('Quiz created!');
      setShowModal(false);
      setFormData(emptyForm);
      onRefresh();
    } catch (error) {
      toast.error('Failed to create quiz');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (quizId) => {
    if (!window.confirm('Delete this quiz?')) return;
    try {
      await api.delete(`/api/quizzes/${quizId}`);
      toast.success('Quiz deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleToggleHide = async (quiz) => {
    try {
      await api.patch(`/api/quizzes/${quiz.id}/visibility`);
      toast.success(quiz.is_hidden ? 'Quiz is now visible to parents' : 'Quiz hidden from parents');
      onRefresh();
    } catch (error) {
      toast.error('Failed to update quiz visibility');
    }
  };

  const openEdit = (quiz) => {
    setEditQuiz(quiz);
    setEditForm({
      title: quiz.title,
      description: quiz.description || '',
      subject_id: quiz.subject_id,
      grade: quiz.grade,
      time_limit_minutes: quiz.time_limit_minutes,
      questions: (quiz.questions || []).map((q) => ({
        question: q.question,
        options: [...q.options],
        correct_answer: q.correct_answer,
        points: q.points
      }))
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setIsEditSubmitting(true);
    try {
      await api.put(`/api/quizzes/${editQuiz.id}`, editForm);
      toast.success('Quiz updated!');
      setEditQuiz(null);
      onRefresh();
    } catch (error) {
      toast.error('Failed to update quiz');
    } finally {
      setIsEditSubmitting(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-fredoka text-xl font-bold text-gray-800">Quizzes</h2>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-sunset-orange text-white px-4 py-2 rounded-xl font-nunito font-semibold hover:bg-orange-600"
        >
          <Plus className="w-5 h-5" /> Create Quiz
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {quizzes.map((quiz) => (
          <div key={quiz.id} data-testid={`quiz-card-${quiz.id}`} className={`bg-white rounded-2xl p-6 shadow-sm ${quiz.is_hidden ? 'opacity-70 border-2 border-dashed border-gray-200' : ''}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-fredoka text-lg font-bold text-gray-800">{quiz.title}</h3>
                  {quiz.is_hidden && (
                    <span data-testid={`quiz-hidden-badge-${quiz.id}`} className="text-xs font-nunito font-semibold bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">Hidden</span>
                  )}
                </div>
                <p className="font-nunito text-gray-500 text-sm">{quiz.subject_name} • {quiz.grade}</p>
                <p className="font-nunito text-sm text-gray-400 mt-2">
                  {quiz.questions?.length || 0} questions • {quiz.total_points} points
                </p>
              </div>
              <div className="flex flex-col gap-1">
                <button
                  data-testid={`quiz-view-btn-${quiz.id}`}
                  onClick={() => setViewQuiz(quiz)}
                  title="View quiz"
                  className="p-2 text-sky-blue hover:bg-sky-50 rounded-lg"
                >
                  <Eye className="w-4 h-4" />
                </button>
                <button
                  data-testid={`quiz-edit-btn-${quiz.id}`}
                  onClick={() => openEdit(quiz)}
                  title="Edit quiz"
                  className="p-2 text-sunset-orange hover:bg-orange-50 rounded-lg"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  data-testid={`quiz-hide-btn-${quiz.id}`}
                  onClick={() => handleToggleHide(quiz)}
                  title={quiz.is_hidden ? 'Unhide quiz' : 'Hide quiz'}
                  className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                >
                  {quiz.is_hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
                <button 
                  data-testid={`quiz-delete-btn-${quiz.id}`}
                  onClick={() => handleDelete(quiz.id)}
                  title="Delete quiz"
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-fredoka text-xl">Create Quiz</DialogTitle>
          </DialogHeader>
          <QuizForm
            formData={formData}
            setFormData={setFormData}
            subjects={subjects}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            submitLabel="Create Quiz"
          />
        </DialogContent>
      </Dialog>

      {/* View Quiz Dialog */}
      <Dialog open={!!viewQuiz} onOpenChange={(open) => !open && setViewQuiz(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quiz-view-dialog">
          {viewQuiz && (
            <>
              <DialogHeader>
                <DialogTitle className="font-fredoka text-xl">{viewQuiz.title}</DialogTitle>
              </DialogHeader>
              <p className="font-nunito text-gray-500 text-sm">
                {viewQuiz.subject_name} • {viewQuiz.grade} • {viewQuiz.time_limit_minutes} min • {viewQuiz.total_points} points
              </p>
              {viewQuiz.description && (
                <p className="font-nunito text-sm text-gray-600">{viewQuiz.description}</p>
              )}
              <div className="space-y-4 mt-2">
                {viewQuiz.questions?.map((q, i) => (
                  <div key={i} className="bg-gray-50 p-4 rounded-xl" data-testid={`quiz-view-question-${i}`}>
                    <p className="font-nunito font-semibold text-gray-800 mb-2">
                      Q{i + 1}. {q.question} <span className="text-gray-400 font-normal">({q.points} pts)</span>
                    </p>
                    <ul className="space-y-1">
                      {q.options.map((opt, j) => (
                        <li
                          key={j}
                          className={`font-nunito text-sm px-3 py-1.5 rounded-lg ${j === q.correct_answer ? 'bg-green-100 text-green-700 font-semibold' : 'text-gray-600'}`}
                        >
                          {opt} {j === q.correct_answer && '✓'}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Quiz Dialog */}
      <Dialog open={!!editQuiz} onOpenChange={(open) => !open && setEditQuiz(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quiz-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-fredoka text-xl">Edit Quiz</DialogTitle>
          </DialogHeader>
          {editForm && (
            <QuizForm
              formData={editForm}
              setFormData={setEditForm}
              subjects={subjects}
              onSubmit={handleEditSubmit}
              isSubmitting={isEditSubmitting}
              submitLabel="Save Changes"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Watch Analytics Tab — per-child per-video watch percentages
function WatchAnalyticsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  useEffect(() => { refresh(); }, []);
  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/admin/watch-analytics');
      setRows(data);
    } catch { toast.error('Failed to load watch analytics'); }
    finally { setLoading(false); }
  };

  const filtered = rows.filter(r => {
    const q = query.toLowerCase().trim();
    if (!q) return true;
    return (r.child_name || '').toLowerCase().includes(q)
        || (r.content_title || '').toLowerCase().includes(q);
  });

  const fmtSec = (s) => {
    const sec = Math.round(s || 0);
    const m = Math.floor(sec / 60);
    const r = sec % 60;
    return `${m}:${String(r).padStart(2, '0')}`;
  };

  return (
    <div data-testid="watch-analytics-tab">
      <div className="flex flex-wrap gap-3 justify-between items-center mb-6">
        <div>
          <h2 className="font-fredoka text-xl font-bold text-gray-800">Video Watch Analytics</h2>
          <p className="font-nunito text-sm text-gray-500">How much of each video every child has really watched.</p>
        </div>
        <div className="flex gap-2">
          <Input
            data-testid="watch-analytics-search"
            placeholder="Search by child or video..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-64"
          />
          <button
            data-testid="watch-analytics-refresh"
            onClick={refresh}
            className="px-4 py-2 rounded-xl bg-sky-blue text-white font-nunito font-semibold hover:bg-sky-600"
          >
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-2xl p-10 text-center shadow-sm">
          <p className="font-nunito text-gray-500">Loading…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl p-10 text-center shadow-sm">
          <Eye className="w-10 h-10 text-gray-200 mx-auto mb-3" />
          <p className="font-nunito text-gray-500">No video watch activity yet.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="watch-analytics-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-3 font-nunito text-xs font-semibold text-gray-500 uppercase">Child</th>
                  <th className="text-left px-4 py-3 font-nunito text-xs font-semibold text-gray-500 uppercase">Video</th>
                  <th className="text-left px-4 py-3 font-nunito text-xs font-semibold text-gray-500 uppercase">Watched</th>
                  <th className="text-left px-4 py-3 font-nunito text-xs font-semibold text-gray-500 uppercase">Progress</th>
                  <th className="text-left px-4 py-3 font-nunito text-xs font-semibold text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr key={i} className="border-t border-gray-100" data-testid={`watch-row-${r.child_id}-${r.content_id}`}>
                    <td className="px-4 py-3 font-nunito text-sm">
                      <div className="font-semibold text-gray-800">{r.child_name}</div>
                      <div className="text-xs text-gray-500">Grade {r.child_grade}</div>
                    </td>
                    <td className="px-4 py-3 font-nunito text-sm text-gray-800">{r.content_title}</td>
                    <td className="px-4 py-3 font-nunito text-sm text-gray-600 whitespace-nowrap">
                      {fmtSec(r.watched_seconds)} / {fmtSec(r.duration)}
                    </td>
                    <td className="px-4 py-3 min-w-[160px]">
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${r.percent >= 90 ? 'bg-green-500' : r.percent >= 50 ? 'bg-sky-blue' : 'bg-orange-400'}`}
                          style={{ width: `${Math.min(100, r.percent)}%` }}
                        />
                      </div>
                      <div className="text-xs font-nunito text-gray-500 mt-1">{r.percent}%</div>
                    </td>
                    <td className="px-4 py-3">
                      {r.completed ? (
                        <span className="inline-flex items-center gap-1 text-xs font-nunito font-semibold bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                          <CheckCircle2 className="w-3 h-3" /> Completed
                        </span>
                      ) : r.percent >= 90 ? (
                        <span className="text-xs font-nunito font-semibold bg-sky-100 text-sky-700 px-2 py-0.5 rounded-full">Unlocked</span>
                      ) : (
                        <span className="text-xs font-nunito font-semibold bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">In progress</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


// Testimonials Tab Component
// Connected to the shared MongoDB `testimonials` collection via the API —
// the same data powers the Landing Page and Parent Dashboard.
function TestimonialsTab() {
  const emptyForm = {
    name: '',
    designation: '',
    company: '',
    message: '',
    rating: 5,
    image: '',
    active: true,
  };

  const fromApi = (t) => ({
    id: t.id,
    name: t.name || '',
    designation: t.designation || '',
    company: t.company || '',
    message: t.message || '',
    rating: Number(t.rating) || 5,
    image: t.image_url || '',
    active: (t.status || 'active') === 'active',
    source: t.source || 'admin',
    createdAt: t.created_at,
    updatedAt: t.updated_at,
  });

  const [testimonials, setTestimonials] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [imageUploading, setImageUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchTestimonials = async () => {
    try {
      const { data } = await api.get('/api/testimonials');
      setTestimonials(Array.isArray(data) ? data.map(fromApi) : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load testimonials');
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAdd = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEdit = (testimonial) => {
    setEditingId(testimonial.id);
    setFormData({
      name: testimonial.name || '',
      designation: testimonial.designation || '',
      company: testimonial.company || '',
      message: testimonial.message || '',
      rating: Number(testimonial.rating) || 5,
      image: testimonial.image || '',
      active: testimonial.active !== false,
    });
    setShowModal(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be smaller than 2MB');
      return;
    }

    setImageUploading(true);
    const reader = new FileReader();
    reader.onload = () => {
      setFormData((prev) => ({ ...prev, image: reader.result }));
      setImageUploading(false);
    };
    reader.onerror = () => {
      toast.error('Failed to read image');
      setImageUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const name = formData.name.trim();
    const message = formData.message.trim();
    if (!name || !message) {
      toast.error('Name and testimonial are required');
      return;
    }
    if (saving) return;
    setSaving(true);

    const payload = {
      name,
      designation: formData.designation,
      company: formData.company,
      message,
      rating: Number(formData.rating) || 5,
      image_url: formData.image || null,
      status: formData.active ? 'active' : 'inactive',
    };

    try {
      if (editingId) {
        const { data } = await api.put(`/api/testimonials/${editingId}`, payload);
        setTestimonials((current) => current.map((item) => (
          item.id === editingId ? fromApi(data) : item
        )));
        toast.success('Testimonial updated successfully');
      } else {
        const { data } = await api.post('/api/testimonials', payload);
        setTestimonials((current) => [fromApi(data), ...current]);
        toast.success('Testimonial added successfully');
      }
      setShowModal(false);
      setEditingId(null);
      setFormData(emptyForm);
    } catch (err) {
      toast.error(err.response?.data?.detail || (editingId ? 'Unable to update testimonial. Please try again.' : 'Unable to add testimonial. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;
    try {
      await api.delete(`/api/testimonials/${id}`);
      setTestimonials((current) => current.filter((item) => item.id !== id));
      toast.success('Testimonial deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to delete testimonial. Please try again.');
    }
  };

  const toggleStatus = async (id) => {
    const target = testimonials.find((item) => item.id === id);
    if (!target) return;
    const newStatus = target.active ? 'inactive' : 'active';
    try {
      const { data } = await api.put(`/api/testimonials/${id}`, { status: newStatus });
      setTestimonials((current) => current.map((item) => (
        item.id === id ? fromApi(data) : item
      )));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to update status. Please try again.');
    }
  };

  const filteredTestimonials = testimonials.filter((item) => {
    if (filter === 'active' && item.active === false) return false;
    if (filter === 'inactive' && item.active !== false) return false;
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return [item.name, item.designation, item.company, item.message]
      .some((value) => (value || '').toLowerCase().includes(q));
  });

  return (
    <div className="space-y-6" data-testid="testimonials-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-fredoka text-xl font-bold text-gray-800">Testimonials</h2>
          <p className="font-nunito text-sm text-gray-500">
            Add, edit, and manage testimonials shown in the website.
          </p>
        </div>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 bg-sunset-orange text-white px-4 py-2 rounded-xl font-nunito font-semibold hover:bg-orange-600"
          data-testid="add-testimonial-btn"
        >
          <Plus className="w-5 h-5" /> Add Testimonial
        </button>
      </div>

      <div className="bg-white rounded-2xl p-4 shadow-sm flex flex-col md:flex-row gap-3">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search testimonials..."
          className="flex-1"
          data-testid="testimonial-search"
        />
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="border rounded-lg px-3 py-2 font-nunito"
          data-testid="testimonial-filter"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {listLoading ? (
        <div className="bg-white rounded-2xl p-10 text-center shadow-sm" data-testid="testimonials-loading">
          <div className="w-8 h-8 border-4 border-sky-blue border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      ) : filteredTestimonials.length === 0 ? (
        <div className="bg-white rounded-2xl p-10 text-center shadow-sm">
          <Star className="w-14 h-14 text-gray-300 mx-auto mb-4" />
          <h3 className="font-fredoka text-xl font-bold text-gray-800 mb-2">
            {testimonials.length === 0 ? 'No testimonials yet' : 'No testimonials found'}
          </h3>
          <p className="font-nunito text-gray-500 mb-5">
            {testimonials.length === 0
              ? 'Add your first testimonial to get started.'
              : 'Try changing your search or status filter.'}
          </p>
          {testimonials.length === 0 && (
            <button
              onClick={openAdd}
              className="inline-flex items-center gap-2 bg-sunset-orange text-white px-4 py-2 rounded-xl font-nunito font-semibold hover:bg-orange-600"
            >
              <Plus className="w-5 h-5" /> Add Testimonial
            </button>
          )}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {filteredTestimonials.map((testimonial) => (
            <div
              key={testimonial.id}
              className="bg-white rounded-2xl p-6 shadow-sm"
              data-testid={`testimonial-card-${testimonial.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  {testimonial.image ? (
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-sunset-orange/10 flex items-center justify-center flex-shrink-0">
                      <User className="w-6 h-6 text-sunset-orange" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <h3 className="font-fredoka text-lg font-bold text-gray-800 truncate">
                      {testimonial.name}
                    </h3>
                    {(testimonial.designation || testimonial.company) && (
                      <p className="font-nunito text-sm text-gray-500 truncate">
                        {[testimonial.designation, testimonial.company].filter(Boolean).join(', ')}
                      </p>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => toggleStatus(testimonial.id)}
                  className={`px-3 py-1 rounded-full text-xs font-nunito font-semibold flex-shrink-0 ${
                    testimonial.active !== false
                      ? 'bg-green-100 text-green-700 hover:bg-green-200'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                  title="Click to change status"
                >
                  {testimonial.active !== false ? 'Active' : 'Inactive'}
                </button>
              </div>

              <div className="flex gap-1 mt-4" aria-label={`${testimonial.rating || 0} out of 5 stars`}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`w-4 h-4 ${
                      star <= Number(testimonial.rating || 0)
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>

              <p className="font-nunito text-gray-600 mt-3 leading-relaxed">
                “{testimonial.message}”
              </p>

              <div className="flex justify-end gap-2 mt-5 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => openEdit(testimonial)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sky-600 hover:bg-sky-50 font-nunito font-semibold text-sm"
                  data-testid={`edit-testimonial-${testimonial.id}`}
                >
                  <Edit className="w-4 h-4" /> Edit
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(testimonial.id)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-red-500 hover:bg-red-50 font-nunito font-semibold text-sm"
                  data-testid={`delete-testimonial-${testimonial.id}`}
                >
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-fredoka text-xl">
              {editingId ? 'Edit Testimonial' : 'Add Testimonial'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="testimonial-name">Name</Label>
              <Input
                id="testimonial-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Parent name"
                required
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="testimonial-designation">Designation</Label>
                <Input
                  id="testimonial-designation"
                  value={formData.designation}
                  onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                  placeholder="Parent / Founder"
                />
              </div>
              <div>
                <Label htmlFor="testimonial-company">Company</Label>
                <Input
                  id="testimonial-company"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="Company name"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="testimonial-message">Testimonial</Label>
              <textarea
                id="testimonial-message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Write the testimonial..."
                className="w-full border rounded-lg px-3 py-2 min-h-[120px] resize-y"
                required
              />
            </div>

            <div>
              <Label>Rating</Label>
              <div className="flex items-center gap-1 mt-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setFormData({ ...formData, rating: star })}
                    className="p-1"
                    aria-label={`Set rating to ${star}`}
                  >
                    <Star
                      className={`w-7 h-7 ${
                        star <= Number(formData.rating)
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-gray-300'
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="testimonial-image">Profile Image</Label>
              <div className="mt-2 flex items-center gap-4">
                {formData.image && (
                  <img
                    src={formData.image}
                    alt="Preview"
                    className="w-14 h-14 rounded-full object-cover"
                  />
                )}
                <label className="cursor-pointer inline-flex items-center gap-2 border border-gray-200 px-4 py-2 rounded-xl font-nunito font-semibold text-gray-700 hover:bg-gray-50">
                  <Upload className="w-4 h-4" />
                  {imageUploading ? 'Uploading...' : 'Choose Image'}
                  <input
                    id="testimonial-image"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={imageUploading}
                    onChange={handleImageChange}
                  />
                </label>
              </div>
              <p className="font-nunito text-xs text-gray-400 mt-1">Maximum 2MB.</p>
            </div>

            <div className="flex items-center justify-between border rounded-xl px-4 py-3">
              <div>
                <p className="font-nunito font-semibold text-gray-800">Status</p>
                <p className="font-nunito text-xs text-gray-500">Show this testimonial as active.</p>
              </div>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, active: !formData.active })}
                className={`w-12 h-7 rounded-full transition-colors relative ${
                  formData.active ? 'bg-sunset-orange' : 'bg-gray-300'
                }`}
                aria-label="Toggle testimonial status"
              >
                <span
                  className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    formData.active ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-4 py-2 rounded-xl font-nunito font-semibold text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={imageUploading || saving}
                className="bg-sunset-orange text-white px-5 py-2 rounded-xl font-nunito font-semibold hover:bg-orange-600 disabled:opacity-50"
                data-testid="testimonial-save-btn"
              >
                {saving ? 'Saving...' : editingId ? 'Update Testimonial' : 'Save Testimonial'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Notifications Tab Component
function NotificationsTab({ showModal, setShowModal }) {
  const [formData, setFormData] = useState({ title: '', message: '', type: 'announcement', target: 'all' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await api.post('/api/notifications', formData);
      toast.success('Notification sent!');
      setShowModal(false);
      setFormData({ title: '', message: '', type: 'announcement', target: 'all' });
    } catch (error) {
      toast.error('Failed to send notification');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-fredoka text-xl font-bold text-gray-800">Send Notifications</h2>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-sunset-orange text-white px-4 py-2 rounded-xl font-nunito font-semibold hover:bg-orange-600"
        >
          <Plus className="w-5 h-5" /> Send Notification
        </button>
      </div>

      <div className="bg-white rounded-2xl p-8 text-center">
        <Bell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="font-fredoka text-xl font-bold text-gray-800 mb-2">Notification Center</h3>
        <p className="font-nunito text-gray-500">
          Send announcements, reminders, and alerts to all parents or specific groups.
        </p>
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-fredoka text-xl">Send Notification</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="message">Message</Label>
              <textarea
                id="message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 min-h-[100px]"
                required
              />
            </div>
            <div>
              <Label htmlFor="type">Type</Label>
              <select
                id="type"
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                className="w-full border rounded-lg px-3 py-2"
              >
                <option value="announcement">Announcement</option>
                <option value="reminder">Reminder</option>
                <option value="alert">Alert</option>
              </select>
            </div>
            <div>
              <Label htmlFor="target">Send To</Label>
              <select
                id="target"
                value={formData.target}
                onChange={(e) => setFormData({ ...formData, target: e.target.value })}
                className="w-full border rounded-lg px-3 py-2"
              >
                <option value="all">All Parents</option>
              </select>
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-sunset-orange text-white py-3 rounded-xl font-nunito font-semibold hover:bg-orange-600 disabled:opacity-50"
            >
              {isSubmitting ? 'Sending...' : 'Send Notification'}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Attendance Tab Component
function AttendanceTab({ classes, childrenList }) {
  const [selectedClassId, setSelectedClassId] = useState('');
  const [records, setRecords] = useState({}); // { child_id: 'present' | 'absent' }
  const [saving, setSaving] = useState(false);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const selectedClass = classes.find((c) => c.id === selectedClassId);
  const eligibleChildren = selectedClass
    ? childrenList.filter((c) => c.grade === selectedClass.grade)
    : [];

  useEffect(() => {
    if (!selectedClassId) return;
    // Load existing attendance for this class
    (async () => {
      try {
        const { data } = await api.get(`/api/attendance?class_id=${selectedClassId}`);
        const next = {};
        for (const rec of data) {
          next[rec.child_id] = rec.status;
        }
        setRecords(next);
      } catch {
        setRecords({});
      }
    })();
  }, [selectedClassId]);

  const setStatus = (childId, status) => {
    setRecords((prev) => ({ ...prev, [childId]: status }));
  };

  const saveAttendance = async () => {
    if (!selectedClassId) return;
    setSaving(true);
    try {
      const entries = Object.entries(records);
      for (const [child_id, status] of entries) {
        await api.post('/api/attendance', { child_id, class_id: selectedClassId, status });
      }
      toast.success(`Attendance saved for ${entries.length} children`);
    } catch {
      toast.error('Failed to save attendance');
    } finally {
      setSaving(false);
    }
  };

  const downloadAttendanceExcel = async () => {
    try {
      const url = selectedClassId
        ? `${BACKEND}/api/reports/attendance/excel?class_id=${selectedClassId}`
        : `${BACKEND}/api/reports/attendance/excel`;
      const token = localStorage.getItem('craven_access_token');
      const res = await fetch(url, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const href = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = 'attendance_report.xlsx';
      a.click();
      window.URL.revokeObjectURL(href);
      toast.success('Excel downloaded');
    } catch {
      toast.error('Failed to download Excel');
    }
  };

  return (
    <div data-testid="attendance-tab">
      <div className="flex flex-wrap gap-3 justify-between items-center mb-6">
        <h2 className="font-fredoka text-xl font-bold text-gray-800">Mark Attendance</h2>
        <button
          onClick={downloadAttendanceExcel}
          className="flex items-center gap-2 bg-apple-green text-white px-4 py-2 rounded-xl font-nunito font-semibold hover:bg-green-700"
          data-testid="attendance-excel-btn"
        >
          <FileSpreadsheet className="w-5 h-5" /> Download Excel
        </button>
      </div>

      <div className="bg-white rounded-2xl p-6 shadow-sm mb-6">
        <Label htmlFor="class-select">Select Class</Label>
        <select
          id="class-select"
          value={selectedClassId}
          onChange={(e) => setSelectedClassId(e.target.value)}
          className="w-full border rounded-lg px-3 py-2 mt-2"
          data-testid="attendance-class-select"
        >
          <option value="">-- Choose a class --</option>
          {classes.map((cls) => (
            <option key={cls.id} value={cls.id}>
              {cls.title} ({cls.grade}) — {new Date(cls.scheduled_at).toLocaleString()}
            </option>
          ))}
        </select>
      </div>

      {selectedClass && (
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h3 className="font-fredoka text-lg font-bold text-gray-800 mb-4">
            {selectedClass.title} — Grade {selectedClass.grade}
          </h3>

          {eligibleChildren.length === 0 ? (
            <p className="font-nunito text-gray-500">No children in this grade yet.</p>
          ) : (
            <>
              <div className="space-y-2 mb-5">
                {eligibleChildren.map((child) => (
                  <div
                    key={child.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-xl"
                    data-testid={`attendance-row-${child.id}`}
                  >
                    <div>
                      <p className="font-nunito font-semibold text-gray-800">{child.name}</p>
                      <p className="font-nunito text-xs text-gray-500">Grade {child.grade}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setStatus(child.id, 'present')}
                        className={`px-4 py-1.5 rounded-full text-sm font-nunito font-semibold ${
                          records[child.id] === 'present'
                            ? 'bg-apple-green text-white'
                            : 'bg-white text-gray-600 border border-gray-200'
                        }`}
                        data-testid={`mark-present-${child.id}`}
                      >
                        Present
                      </button>
                      <button
                        onClick={() => setStatus(child.id, 'absent')}
                        className={`px-4 py-1.5 rounded-full text-sm font-nunito font-semibold ${
                          records[child.id] === 'absent'
                            ? 'bg-sunset-orange text-white'
                            : 'bg-white text-gray-600 border border-gray-200'
                        }`}
                        data-testid={`mark-absent-${child.id}`}
                      >
                        Absent
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <button
                onClick={saveAttendance}
                disabled={saving || Object.keys(records).length === 0}
                className="bg-sunset-orange text-white px-6 py-2.5 rounded-full font-nunito font-semibold hover:bg-orange-600 disabled:opacity-50"
                data-testid="save-attendance-btn"
              >
                {saving ? 'Saving...' : 'Save Attendance'}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}


// ------------ CSV Import Button ------------
function CsvImportButton({ endpoint, label, testid, template, onDone }) {
  const [busy, setBusy] = useState(false);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please upload a .csv file');
      e.target.value = '';
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${BACKEND}${endpoint}`, {
        method: 'POST',
        credentials: 'include',
        headers: (() => { const t = localStorage.getItem('craven_access_token'); return t ? { Authorization: `Bearer ${t}` } : {}; })(),
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Import failed');
      }
      const data = await res.json();
      let msg = `Imported ${data.created ?? 0}`;
      if (typeof data.skipped === 'number') msg += ` (skipped ${data.skipped})`;
      if (data.errors?.length) msg += ` — ${data.errors.length} error${data.errors.length !== 1 ? 's' : ''}`;
      toast.success(msg);
      if (data.errors?.length) console.warn('CSV errors:', data.errors);
      onDone?.();
    } catch (err) {
      toast.error(err.message || 'Import failed');
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  };

  return (
    <label
      className={`cursor-pointer flex items-center gap-2 px-3 py-2 rounded-xl border-2 border-dashed font-nunito font-semibold text-sm transition-colors ${
        busy ? 'border-slate-200 text-slate-400 cursor-wait' : 'border-slate-200 text-slate-600 hover:border-sunset-orange hover:text-sunset-orange'
      }`}
      title={`CSV columns: ${template}`}
      data-testid={testid}
    >
      <Upload className="w-4 h-4" />
      {busy ? 'Uploading...' : label}
      <input
        type="file"
        accept=".csv"
        className="hidden"
        disabled={busy}
        onChange={handleFile}
        data-testid={`${testid}-input`}
      />
    </label>
  );
}



// ============== PAYMENTS TAB ==============
function PaymentsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | active | inactive
  const [overrideTarget, setOverrideTarget] = useState(null); // row being edited
  const [action, setAction] = useState('grant'); // grant | extend | revoke
  const [days, setDays] = useState(30);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/admin/subscriptions');
      setRows(data);
    } catch { toast.error('Failed to load subscriptions'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = rows.filter((r) => {
    if (filter === 'active' && !r.is_active) return false;
    if (filter === 'inactive' && r.is_active) return false;
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (r.parent_name || '').toLowerCase().includes(q) ||
      (r.parent_email || '').toLowerCase().includes(q) ||
      (r.child_name || '').toLowerCase().includes(q)
    );
  });

  const stats = {
    total: rows.length,
    active: rows.filter((r) => r.is_active).length,
    inactive: rows.filter((r) => !r.is_active).length,
    admin_granted: rows.filter((r) => r.source === 'admin_override').length,
  };

  const openOverride = (row, defaultAction = 'grant') => {
    setOverrideTarget(row);
    setAction(defaultAction);
    setDays(30);
    setNotes('');
  };

  const submit = async () => {
    if (!overrideTarget) return;
    setSaving(true);
    try {
      const { data } = await api.post('/api/admin/subscriptions/override', {
        child_id: overrideTarget.child_id,
        action,
        days: parseInt(days, 10) || 30,
        notes,
      });
      toast.success(data.message);
      setOverrideTarget(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Override failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="payments-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-fredoka text-2xl font-bold text-gray-800">Payments & Subscriptions</h2>
          <p className="font-nunito text-sm text-gray-500">Manage subscription status for every family — grant, extend, or revoke access.</p>
        </div>
        <button
          onClick={load}
          className="px-4 py-2 rounded-full border border-gray-200 hover:border-sunset-orange text-gray-600 hover:text-sunset-orange font-fredoka text-sm transition-all"
          data-testid="refresh-subscriptions"
        >
          Refresh
        </button>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total children', value: stats.total, color: 'from-slate-100 to-slate-50', text: 'text-slate-700' },
          { label: 'Active subscriptions', value: stats.active, color: 'from-[#DCFCE7] to-[#F0FDF4]', text: 'text-[#16A34A]' },
          { label: 'Inactive / expired', value: stats.inactive, color: 'from-[#FEF3C7] to-[#FEF9C3]', text: 'text-[#CA8A04]' },
          { label: 'Admin-granted', value: stats.admin_granted, color: 'from-[#DBEAFE] to-[#F0F9FF]', text: 'text-[#0284C7]' },
        ].map((s) => (
          <div key={s.label} className={`bg-gradient-to-br ${s.color} rounded-2xl p-4 border border-white`}>
            <p className={`font-fredoka text-3xl font-bold ${s.text}`}>{s.value}</p>
            <p className="font-nunito text-xs text-gray-600 mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by parent, email, or child…"
          className="flex-1 min-w-[240px] rounded-full border border-gray-200 focus:border-sunset-orange focus:outline-none px-4 py-2.5 font-nunito text-sm"
          data-testid="payments-search"
        />
        <div className="flex gap-1 bg-white rounded-full border border-gray-200 p-1">
          {[
            { id: 'all', label: 'All' },
            { id: 'active', label: 'Active' },
            { id: 'inactive', label: 'Inactive' },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-4 py-1.5 rounded-full font-fredoka text-sm transition-all ${
                filter === f.id ? 'bg-sunset-orange text-white' : 'text-gray-600 hover:bg-gray-50'
              }`}
              data-testid={`payments-filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-16 flex justify-center">
            <motion.span
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              className="w-6 h-6 border-2 border-sunset-orange border-t-transparent rounded-full"
            />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center">
            <CreditCard className="w-12 h-12 text-gray-200 mx-auto mb-3" />
            <p className="font-nunito text-gray-400">No records found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  {['Parent', 'Child', 'Status', 'Source', 'Expires', 'Actions'].map((h) => (
                    <th key={h} className="text-left px-5 py-3 font-fredoka text-xs uppercase tracking-wider text-gray-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={`${r.parent_id}-${r.child_id}`} className="border-b border-gray-50 hover:bg-gray-50/50" data-testid={`payment-row-${r.child_id}`}>
                    <td className="px-5 py-4">
                      <p className="font-fredoka font-semibold text-gray-800 text-sm">{r.parent_name}</p>
                      <p className="font-nunito text-xs text-gray-500">{r.parent_email}</p>
                    </td>
                    <td className="px-5 py-4">
                      <p className="font-fredoka text-sm text-gray-800">{r.child_name}</p>
                      <p className="font-nunito text-xs text-gray-500">Grade {r.child_grade}</p>
                    </td>
                    <td className="px-5 py-4">
                      {r.is_active ? (
                        <span className="inline-flex items-center gap-1 bg-[#DCFCE7] text-[#16A34A] px-2.5 py-1 rounded-full text-xs font-fredoka font-semibold">
                          <CheckCircle2 className="w-3 h-3" /> Active
                        </span>
                      ) : r.has_subscription ? (
                        <span className="inline-flex items-center gap-1 bg-[#FEE2E2] text-[#DC2626] px-2.5 py-1 rounded-full text-xs font-fredoka font-semibold">
                          <AlertCircle className="w-3 h-3" /> {r.status === 'revoked' ? 'Revoked' : 'Expired'}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-500 px-2.5 py-1 rounded-full text-xs font-fredoka font-semibold">
                          None
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <span className={`text-xs font-nunito ${r.source === 'admin_override' ? 'text-[#0284C7]' : 'text-gray-500'}`}>
                        {r.source === 'admin_override' ? '🛡️ Admin' : r.source === 'stripe' ? '💳 Stripe' : '—'}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      {r.expires_at ? (
                        <>
                          <p className="font-nunito text-sm text-gray-700">{new Date(r.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                          {r.is_active && <p className="font-nunito text-xs text-gray-400">{r.days_remaining} days left</p>}
                        </>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex gap-2">
                        {!r.is_active ? (
                          <button
                            onClick={() => openOverride(r, 'grant')}
                            className="px-3 py-1.5 rounded-full bg-sunset-orange hover:bg-[#E84E44] text-white font-fredoka text-xs font-semibold"
                            data-testid={`grant-${r.child_id}`}
                          >
                            Grant
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={() => openOverride(r, 'extend')}
                              className="px-3 py-1.5 rounded-full bg-sky-blue hover:bg-[#0EA5E9] text-white font-fredoka text-xs font-semibold"
                              data-testid={`extend-${r.child_id}`}
                            >
                              Extend
                            </button>
                            <button
                              onClick={() => openOverride(r, 'revoke')}
                              className="px-3 py-1.5 rounded-full border border-red-200 hover:bg-red-50 text-red-600 font-fredoka text-xs font-semibold"
                              data-testid={`revoke-${r.child_id}`}
                            >
                              Revoke
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Override Dialog */}
      <Dialog open={!!overrideTarget} onOpenChange={(v) => { if (!v) setOverrideTarget(null); }}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="override-dialog">
          <DialogHeader>
            <DialogTitle className="font-fredoka text-xl text-gray-800 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-sunset-orange" />
              {action === 'grant' && 'Grant subscription'}
              {action === 'extend' && 'Extend subscription'}
              {action === 'revoke' && 'Revoke subscription'}
            </DialogTitle>
          </DialogHeader>
          {overrideTarget && (
            <div className="space-y-4 pt-2">
              <div className="bg-gray-50 rounded-2xl p-4">
                <p className="font-fredoka font-semibold text-gray-800">{overrideTarget.child_name} · Grade {overrideTarget.child_grade}</p>
                <p className="font-nunito text-sm text-gray-600">Parent: {overrideTarget.parent_name} ({overrideTarget.parent_email})</p>
                {overrideTarget.expires_at && (
                  <p className="font-nunito text-xs text-gray-500 mt-2">
                    Current expiry: {new Date(overrideTarget.expires_at).toLocaleDateString()}
                  </p>
                )}
              </div>

              {action !== 'revoke' && (
                <div>
                  <label htmlFor="ov-days" className="block text-xs font-nunito font-semibold text-gray-600 mb-1.5">
                    {action === 'grant' ? 'Grant access for (days)' : 'Extend by (days)'}
                  </label>
                  <div className="flex gap-2 items-center">
                    <input
                      id="ov-days"
                      type="number"
                      min={1}
                      max={365}
                      value={days}
                      onChange={(e) => setDays(e.target.value)}
                      className="w-full rounded-xl border-2 border-gray-100 focus:border-sunset-orange focus:outline-none py-2.5 px-3 font-nunito text-sm"
                      data-testid="override-days"
                    />
                    <span className="font-nunito text-sm text-gray-500 whitespace-nowrap">days</span>
                  </div>
                  <div className="flex gap-2 mt-2">
                    {[7, 30, 90, 365].map((d) => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setDays(d)}
                        className={`px-3 py-1 rounded-full text-xs font-fredoka font-medium transition-all ${
                          Number(days) === d
                            ? 'bg-sunset-orange text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {d === 365 ? '1 year' : `${d}d`}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label htmlFor="ov-notes" className="block text-xs font-nunito font-semibold text-gray-600 mb-1.5">
                  Notes <span className="text-gray-400 font-normal">(internal, optional)</span>
                </label>
                <textarea
                  id="ov-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  placeholder="e.g., Comp'd account, offline cash payment, scholarship…"
                  className="w-full rounded-xl border-2 border-gray-100 focus:border-sunset-orange focus:outline-none py-2.5 px-3 font-nunito text-sm resize-none"
                  data-testid="override-notes"
                />
              </div>

              {action === 'revoke' && (
                <div className="bg-red-50 border border-red-100 rounded-xl px-3 py-2.5 text-sm font-nunito text-red-700">
                  ⚠️ The parent will lose access to lessons, quizzes, and live classes immediately.
                </div>
              )}

              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => setOverrideTarget(null)}
                  disabled={saving}
                  className="flex-1 px-4 py-2.5 rounded-full border-2 border-gray-100 text-gray-600 font-fredoka font-medium text-sm hover:bg-gray-50 disabled:opacity-50"
                  data-testid="override-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={submit}
                  disabled={saving}
                  className={`flex-1 px-4 py-2.5 rounded-full text-white font-fredoka font-medium text-sm disabled:opacity-50 ${
                    action === 'revoke' ? 'bg-red-500 hover:bg-red-600' : 'bg-sunset-orange hover:bg-[#E84E44]'
                  }`}
                  data-testid="override-confirm"
                >
                  {saving ? 'Saving…' : action === 'revoke' ? 'Revoke access' : action === 'extend' ? 'Extend subscription' : 'Grant subscription'}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============== CONTENT PREVIEW (inline in admin content form) ==============
function ContentPreview({ url, contentType, tall = false }) {
  if (!url || !url.trim()) {
    return (
      <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-center gap-3" data-testid="content-preview-empty">
        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
        <div>
          <p className="font-fredoka text-sm font-semibold text-amber-800">No file attached</p>
          <p className="font-nunito text-xs text-amber-700">This content has an empty URL. Please re-upload the file or paste a valid link.</p>
        </div>
      </div>
    );
  }
  // Detect YouTube / Vimeo for URL embed mode
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
  const vimeo = url.match(/vimeo\.com\/(\d+)/);

  let embedNode = null;
  if (contentType === 'video' && yt) {
    embedNode = (
      <iframe
        src={`https://www.youtube.com/embed/${yt[1]}`}
        title="Preview"
        className="w-full h-full rounded-lg"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        data-testid="content-preview-youtube"
      />
    );
  } else if (contentType === 'video' && vimeo) {
    embedNode = (
      <iframe
        src={`https://player.vimeo.com/video/${vimeo[1]}`}
        title="Preview"
        className="w-full h-full rounded-lg"
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
        data-testid="content-preview-vimeo"
      />
    );
  } else if (contentType === 'video') {
    // Direct video file (mp4/webm/etc.)
    embedNode = (
      <video src={url} controls className="w-full h-full rounded-lg bg-black" data-testid="content-preview-video">
        Sorry, your browser can't preview this video.
      </video>
    );
  } else if (contentType === 'pdf') {
    // <iframe> is more reliable than <object> for PDF rendering across browsers.
    // The browser's built-in PDF viewer handles the frame's content directly.
    embedNode = (
      <div className="relative w-full h-full">
        <iframe
          src={`${url}#view=FitH&toolbar=1`}
          title="PDF preview"
          className="w-full h-full rounded-lg bg-white border border-gray-100"
          data-testid="content-preview-pdf"
        />
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute bottom-3 right-3 inline-flex items-center gap-1 bg-sunset-orange hover:bg-orange-600 text-white px-3 py-1.5 rounded-full font-fredoka text-xs shadow-md"
          data-testid="content-preview-pdf-open"
        >
          Open in new tab ↗
        </a>
      </div>
    );
  } else {
    embedNode = (
      <div className="flex items-center justify-center h-full text-gray-500 text-sm">
        <a href={url} target="_blank" rel="noopener noreferrer" className="text-sunset-orange underline">
          Open link ↗
        </a>
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-xl border border-gray-100 bg-gray-50 p-3 h-full flex flex-col" data-testid="content-preview">
      <div className="flex items-center justify-between mb-2">
        <p className="font-fredoka text-xs font-semibold text-gray-600 uppercase tracking-wider">Preview</p>
        <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs font-nunito text-sunset-orange hover:underline">
          Open in new tab ↗
        </a>
      </div>
      <div className={`${tall ? 'flex-1 min-h-[400px]' : 'h-64'} rounded-lg overflow-hidden bg-white`}>
        {embedNode}
      </div>
    </div>
  );
}



// Pricing Tab Component — admin edits monthly subscription price + description
function PricingTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [meta, setMeta] = useState(null);

  const fetchPricing = async () => {
    try {
      const { data } = await api.get('/api/admin/pricing');
      setMeta(data);
      setAmount(String(data.amount));
      setDescription(data.description || '');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load pricing');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPricing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    const numeric = parseFloat(amount);
    if (Number.isNaN(numeric) || numeric < 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!description.trim()) {
      toast.error('Description is required');
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put('/api/admin/pricing', {
        amount: numeric,
        description: description.trim(),
      });
      setMeta(data);
      setAmount(String(data.amount));
      setDescription(data.description);
      toast.success('Pricing updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save pricing');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="pricing-loading">
        <div className="w-8 h-8 border-4 border-sky-blue border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl" data-testid="pricing-tab">
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-sunny-yellow/20 to-sunset-orange/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-sunset-orange/15 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-sunset-orange" />
            </div>
            <div>
              <h2 className="font-fredoka text-xl text-gray-900">Subscription Pricing</h2>
              <p className="font-nunito text-sm text-gray-600">
                Update the monthly plan price shown to parents at checkout.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSave} className="p-6 space-y-5">
          <div>
            <Label className="font-nunito text-sm text-gray-700">Plan name</Label>
            <Input
              value={meta?.name || ''}
              disabled
              className="mt-1 bg-gray-50 text-gray-500"
              data-testid="pricing-plan-name"
            />
            <p className="mt-1 text-xs text-gray-500 font-nunito">Plan name is fixed.</p>
          </div>

          <div>
            <Label className="font-nunito text-sm text-gray-700">Price (USD / month)</Label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-fredoka">$</span>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="pl-7"
                data-testid="pricing-amount-input"
                required
              />
            </div>
            <p className="mt-1 text-xs text-gray-500 font-nunito">
              This is the amount charged per child per month via Stripe.
            </p>
          </div>

          <div>
            <Label className="font-nunito text-sm text-gray-700">Description</Label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-nunito focus:outline-none focus:ring-2 focus:ring-sky-blue"
              data-testid="pricing-description-input"
              required
            />
            <p className="mt-1 text-xs text-gray-500 font-nunito">
              Shown under the price on the parent Subscribe card.
            </p>
          </div>

          <div className="flex items-center justify-between pt-2">
            <button
              type="button"
              onClick={() => {
                if (meta) {
                  setAmount(String(meta.amount));
                  setDescription(meta.description);
                }
              }}
              className="font-nunito text-sm text-gray-600 hover:text-gray-800"
              data-testid="pricing-reset-btn"
            >
              Reset changes
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 bg-sunset-orange hover:bg-sunset-orange/90 disabled:opacity-60 text-white font-fredoka px-6 py-2.5 rounded-full transition"
              data-testid="pricing-save-btn"
            >
              {saving ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>

      <div className="mt-4 p-4 rounded-xl bg-sky-50 border border-sky-100 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-sky-600 flex-shrink-0 mt-0.5" />
        <div className="font-nunito text-sm text-sky-900">
          <div className="font-semibold">Heads up</div>
          <div className="text-sky-800">
            Existing subscriptions keep their original price. New checkouts and renewals will use the updated price immediately.
          </div>
        </div>
      </div>
    </div>
  );
}